import React, { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faImage, faReply, faUser, faPhone, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import CustomAudioPlayer from './CustomAudioPlayer';
import { parseMessageData, formatWhatsAppText } from '../../utils';

/**
 * Componente con estado de carga (loader) para imágenes de WhatsApp
 */
const MessageImage = ({ mediaUrl, isDark, onOpenLightbox, onMediaLoad }) => {
  const isBlob = mediaUrl?.startsWith('blob:') || mediaUrl?.startsWith('data:');
  const [isLoading, setIsLoading] = useState(!isBlob);
  const [hasError, setHasError] = useState(false);

  return (
    <div className="mb-1 text-center overflow-hidden rounded-2 position-relative" style={{ minWidth: '180px' }}>
      {isLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-2 rounded-2"
          style={{
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.08)',
            zIndex: 2,
            minHeight: '120px'
          }}
        >
          <FontAwesomeIcon icon={faSpinner} spin className="text-primary mb-1" style={{ fontSize: '1.2rem' }} />
        </div>
      )}

      {hasError ? (
        <div
          className="d-flex align-items-center justify-content-center p-3 rounded-2 gap-2"
          style={{
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.06)',
            color: '#ef4444',
            fontSize: '0.78rem'
          }}
        >
          <FontAwesomeIcon icon={faImage} />
          <span>No se pudo cargar la imagen</span>
        </div>
      ) : (
        <img
          src={mediaUrl}
          alt="Imagen de WhatsApp"
          className="img-fluid rounded-2"
          style={{
            maxHeight: '280px',
            width: '100%',
            objectFit: 'cover',
            cursor: 'zoom-in',
            display: 'block'
          }}
          onClick={() => onOpenLightbox(mediaUrl)}
          onLoad={() => {
            setIsLoading(false);
            if (onMediaLoad) onMediaLoad();
          }}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      )}
    </div>
  );
};

/**
 * Componente con estado de carga para stickers
 */
const StickerImage = ({ mediaUrl, isDark, onOpenLightbox, onMediaLoad }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="position-relative" style={{ width: '130px', height: '130px' }}>
      {isLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
          style={{ backgroundColor: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)' }}
        >
          <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '1.2rem' }} />
        </div>
      )}
      <img
        src={mediaUrl}
        alt="Sticker de WhatsApp"
        style={{
          width: '130px',
          height: '130px',
          objectFit: 'contain',
          cursor: 'pointer',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease'
        }}
        onClick={() => onOpenLightbox(mediaUrl)}
        onLoad={() => {
          setIsLoading(false);
          if (onMediaLoad) onMediaLoad();
        }}
        onError={() => setIsLoading(false)}
      />
    </div>
  );
};

/**
 * Subcomponente para renderizar cada burbuja de mensaje individual
 */
const TicketMessageItem = ({ msg, index, messages = [], onOpenLightbox, onMediaLoad, onReply, onOpenContext }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [isHovered, setIsHovered] = useState(false);
  const {
    isAgent,
    author,
    text,
    timeStr,
    isSticker,
    isImage,
    isAudio,
    isVideo,
    isDocument,
    isContact,
    mediaUrl,
    quotedText,
    quotedAuthor,
    quotedIsAgent,
    quotedMessageTypeId,
    quotedMessageId,
    isNote
  } = parseMessageData(msg, index, messages);

  if (isSticker && mediaUrl) {
    return (
      <div
        className={`d-flex flex-column ${isAgent ? 'align-items-end' : 'align-items-start'} position-relative`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="position-relative">
          <StickerImage
            mediaUrl={mediaUrl}
            isDark={isDark}
            onOpenLightbox={onOpenLightbox}
            onMediaLoad={onMediaLoad}
          />
          {onReply && isHovered && !msg.isTemp && (
            <button
              type="button"
              className="btn btn-sm p-1 rounded-circle border-0 position-absolute shadow-sm"
              style={{
                top: '4px',
                [isAgent ? 'left' : 'right']: '-28px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#94a3b8' : '#64748b',
                zIndex: 5
              }}
              onClick={() => onReply(msg)}
              title="Responder mensaje"
            >
              <FontAwesomeIcon icon={faReply} style={{ fontSize: '0.70rem' }} />
            </button>
          )}
          <div className="d-flex align-items-center justify-content-end gap-1 mt-1" style={{ fontSize: '0.68rem', color: isDark ? '#8696a0' : '#64748b' }}>
            <span className="fw-medium">{author}</span>
            <span>•</span>
            <span>{timeStr}</span>
            {isAgent && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.65rem' }} />}
          </div>
        </div>
      </div>
    );
  }

  const bubbleBg = isNote
    ? (isDark ? 'rgba(245, 158, 11, 0.16)' : '#fef3c7')
    : (isDark
      ? (isAgent ? '#005c4b' : '#202c33')
      : (isAgent ? '#d9fdd3' : '#ffffff'));

  const bubbleColor = isNote
    ? (isDark ? '#fef08a' : '#78350f')
    : (isDark ? '#e9edef' : '#111b21');

  const bubbleBorder = isNote
    ? (isDark ? '1px solid rgba(245, 158, 11, 0.38)' : '1px solid #fde68a')
    : (isDark
      ? (isAgent ? 'none' : '1px solid #2a3942')
      : (isAgent ? 'none' : '1px solid #e9edef'));

  const authorColor = isNote
    ? (isDark ? '#fbbf24' : '#92400e')
    : (isDark
      ? (isAgent ? '#d1fae5' : '#38bdf8')
      : '#111b21');

  const hasQuote = Boolean(quotedText || quotedMessageId || quotedMessageTypeId);

  return (
    <div
      className={`d-flex flex-column ${isAgent ? 'align-items-end' : 'align-items-start'} mb-1 position-relative`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="position-relative shadow-sm"
        style={{
          maxWidth: '70%',
          minWidth: '120px',
          backgroundColor: bubbleBg,
          color: bubbleColor,
          borderRadius: isAgent ? '8px 0px 8px 8px' : '0px 8px 8px 8px',
          border: bubbleBorder,
          padding: '8px 12px 6px 12px',
          fontSize: '0.88rem',
          lineHeight: '1.4'
        }}
      >
        {/* Botón flotante para responder al pasar el cursor (Estilo WhatsApp Web) */}
        {onReply && isHovered && !msg.isTemp && !isNote && (
          <button
            type="button"
            className="btn btn-sm p-1 rounded-circle border-0 position-absolute shadow-sm"
            style={{
              top: '4px',
              [isAgent ? 'left' : 'right']: '-28px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              color: isDark ? '#94a3b8' : '#64748b',
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              zIndex: 5
            }}
            onClick={(e) => {
              e.stopPropagation();
              onReply(msg);
            }}
            title="Responder mensaje"
          >
            <FontAwesomeIcon icon={faReply} style={{ fontSize: '0.68rem' }} />
          </button>
        )}

        {/* Encabezado distintivo para Notas Internas */}
        {isNote && (
          <div
            className="d-flex align-items-center gap-1 mb-1 fw-bold"
            style={{
              fontSize: '0.72rem',
              color: isDark ? '#fbbf24' : '#b45309',
              letterSpacing: '0.02em',
              userSelect: 'none'
            }}
          >
            <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '0.75rem' }} />
            <span>Nota interna</span>
          </div>
        )}

        {/* Bloque de Mensaje Citado (Quoted Message estilo WhatsApp Web) */}
        {hasQuote && (
            <div
                className="rounded-2 p-2 mb-2 text-start"
                style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.05)',
                    borderLeft: `4px solid ${quotedIsAgent ? '#25d366' : '#38bdf8'}`,
                    userSelect: 'none'
                }}
            >
                {/* Cabecera de la cita con el botón "Ver conversación" */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <span
                        className="fw-bold text-truncate"
                        style={{
                            fontSize: '0.74rem',
                            color: quotedIsAgent ? (isDark ? '#4ade80' : '#059669') : (isDark ? '#38bdf8' : '#0284c7')
                        }}
                    >
                        {quotedIsAgent ? (quotedAuthor && quotedAuthor !== 'AKASIABOT' ? quotedAuthor : 'Asesor') : 'Cliente'}
                    </span>

                    {/* BOTÓN VER CONVERSACIÓN */}
                    {onOpenContext && (quotedMessageId || msg.idMensaje) && (
                        <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none shadow-none"
                            style={{
                                fontSize: '0.70rem',
                                color: isDark ? '#38bdf8' : '#0284c7',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenContext(quotedMessageId || msg.idMensaje);
                            }}
                            title="Ver contexto completo de esta conversación"
                        >
                            Ver conversación
                        </button>
                    )}
                </div>
                <div
                    className="text-truncate"
                    style={{
                        fontSize: '0.77rem',
                        color: isDark ? '#cbd5e1' : '#475569',
                        maxHeight: '36px',
                        lineHeight: '1.3'
                    }}
                >
                    {quotedMessageTypeId === 2 ? '📷 Foto'
                        : quotedMessageTypeId === 3 ? '💟 Sticker'
                            : quotedMessageTypeId === 4 ? '🎤 Mensaje de voz'
                                : quotedMessageTypeId === 5 ? '🎥 Video'
                                    : quotedMessageTypeId === 6 ? '📄 Documento'
                                        : (quotedText || 'Mensaje')}
                </div>
            </div>
        )}

        {/* 1. Render Imagen con visor y loading */}
        {isImage && mediaUrl && (
          <MessageImage
            mediaUrl={mediaUrl}
            isDark={isDark}
            onOpenLightbox={onOpenLightbox}
            onMediaLoad={onMediaLoad}
          />
        )}

        {/* 2. Render Video */}
        {isVideo && mediaUrl && (
          <div className="mb-1 overflow-hidden rounded-2">
            <video controls preload="metadata" className="w-100" style={{ maxHeight: '280px' }} src={mediaUrl} />
          </div>
        )}

        {/* 3. Render Audio / Nota de voz estilizada */}
        {isAudio && mediaUrl && (
          <div className="mb-1">
            <CustomAudioPlayer src={mediaUrl} isAgent={isAgent} />
          </div>
        )}

        {/* 4. Render Documento */}
        {isDocument && mediaUrl && (
          <div
            className="mb-1 p-2 rounded d-flex align-items-center justify-content-between gap-2 border"
            style={{
              backgroundColor: isDark ? '#182635' : '#f8fafc',
              borderColor: isDark ? '#2a3942' : '#e2e8f0',
              color: bubbleColor
            }}
          >
            <span className="text-truncate" style={{ fontSize: '0.8rem' }}>
              📄 {text || 'Documento adjunto'}
            </span>
            <a href={mediaUrl} download target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary px-2 py-0" style={{ fontSize: '0.75rem' }}>
              Descargar
            </a>
          </div>
        )}

        {/* 5. Render Contacto compartido (idCatTipoMensaje === 8) */}
        {isContact && (
          <div className="d-flex flex-column gap-2 mb-1" style={{ minWidth: '200px' }}>
            {(() => {
              const cards = (text || '')
                .split('\n---\n')
                .map(c => {
                  const lines = c.split('\n').map(l => l.trim()).filter(Boolean);
                  return {
                    name: lines[0] || 'Contacto',
                    phone: lines[1] || ''
                  };
                })
                .filter(c => c.name || c.phone);

              if (cards.length === 0) {
                return (
                  <div className="text-muted fst-italic" style={{ fontSize: '0.82rem' }}>
                    👤 Contacto compartido
                  </div>
                );
              }

              return cards.map((card, cIdx) => (
                <div
                  key={cIdx}
                  className="p-2 rounded border"
                  style={{
                    backgroundColor: isDark ? '#182635' : '#f8fafc',
                    borderColor: isDark ? '#2a3942' : '#e2e8f0',
                    color: bubbleColor
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#059669',
                        fontSize: '0.9rem',
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div className="overflow-hidden">
                      <div className="fw-bold text-truncate" style={{ fontSize: '0.88rem' }}>
                        {card.name}
                      </div>
                      {card.phone && (
                        <div className="text-muted text-truncate" style={{ fontSize: '0.78rem' }}>
                          {card.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  {card.phone && (
                    <div className="d-flex gap-1 border-top pt-2" style={{ borderColor: isDark ? '#2a3942' : '#e2e8f0' }}>
                      <a
                        href={`tel:${card.phone.replace(/[^\d+]/g, '')}`}
                        className="btn btn-sm btn-link text-decoration-none p-0 flex-grow-1 text-center"
                        style={{ fontSize: '0.75rem', color: isDark ? '#38bdf8' : '#0284c7' }}
                      >
                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                        {card.phone}
                      </a>
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        )}

        {/* Caption o texto del mensaje */}
        {!isContact && text && !text.startsWith('[') && (
          <div className="mb-1 text-break" style={{ whiteSpace: 'pre-wrap' }}>
            {formatWhatsAppText(text)}
          </div>
        )}

        {/* Footer del mensaje estilo WhatsApp: Autor (si es agente) + Hora + Check */}
        <div
          className="d-flex align-items-center justify-content-end gap-1 mt-1"
          style={{ fontSize: '0.68rem', color: isDark ? '#8696a0' : '#64748b', userSelect: 'none' }}
        >
          {author && (
            <>
              <span className="fw-bold" style={{ fontSize: '0.68rem', color: authorColor }}>{author}</span>
              <span>•</span>
            </>
          )}
          <span>{timeStr}</span>
          {isAgent && !isNote && (
            msg.isTemp ? (
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.62rem', color: isDark ? '#94a3b8' : '#64748b', marginLeft: '2px' }} title="Enviando a WhatsApp..." />
            ) : (
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.62rem', color: '#53bdeb', marginLeft: '2px' }} />
            )
          )}
        </div>
      </div>
    </div>
  );
};
export default TicketMessageItem;
